import { useEffect, useState, useMemo } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAppStore } from '@store/appStore'
import { usePWAUpdate } from '@hooks/usePWAUpdate'
import { MorphViewerPage } from '@components/GestureViewer/MorphViewerPage'
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary'
import { DatasetsManifest } from './types/dataset'
import { apiUrl, initBlobDirectAccess } from './config'

interface AppProps {
  initialShopId?: string | null
}

function App({ initialShopId }: AppProps) {
  usePWAUpdate() // Silent auto-update: SW controllerchange + version.json polling
  const navigate = useNavigate()
  const currentShopId = useAppStore((state) => state.currentShopId)
  const availableDatasets = useAppStore((state) => state.availableDatasets)
  const setAvailableShops = useAppStore((state) => state.setAvailableShops)
  const setAvailableDatasets = useAppStore((state) => state.setAvailableDatasets)
  const setIsLoadingDatasets = useAppStore((state) => state.setIsLoadingDatasets)
  
  // Track whether datasets fetch has completed (not just started)
  const [datasetsReady, setDatasetsReady] = useState(false)
  
  // Force re-render when URL changes (for back button navigation)
  const [urlKey, setUrlKey] = useState(0)
  
  // Listen for URL changes to force re-renders
  useEffect(() => {
    const handleUrlChange = () => setUrlKey(prev => prev + 1)
    window.addEventListener('popstate', handleUrlChange)
    window.addEventListener('urlChanged', handleUrlChange)
    return () => {
      window.removeEventListener('popstate', handleUrlChange)
      window.removeEventListener('urlChanged', handleUrlChange)
    }
  }, [])
  
  // Read current URL parameters (reactive to URL changes)
  const effectiveShopId = useMemo(() => {
    const currentUrlParams = new URLSearchParams(window.location.search)
    const currentShopIdFromUrl = currentUrlParams.get('shopid')
    return currentShopId || currentShopIdFromUrl
  }, [currentShopId, urlKey])

  // Load datasets manifest on app mount
  useEffect(() => {
    const loadDatasets = async () => {
      setIsLoadingDatasets(true)
      try {
        console.log('📦 [App] Loading datasets manifest...')
        
        // Load from API (Cosmos DB)
        // Include auth headers so private shop datasets are returned
        const authToken = localStorage.getItem('dukkan_token')
        const headers: Record<string, string> = {}
        if (authToken) headers['Authorization'] = `Bearer ${authToken}`
        let response = await fetch(apiUrl('/api/datasets'), { headers })
        let source = 'API (Cosmos DB)'
        
        if (!response.ok) throw new Error('Failed to load datasets manifest')
        
        const manifest: DatasetsManifest = await response.json()
        console.log(`📦 [App] Manifest loaded from ${source}:`, {
          total: manifest.total_datasets,
          datasets: manifest.datasets.map(d => ({ id: d.id, name: d.name, cameras: d.camera_count }))
        })
        
        // Initialize direct blob access BEFORE setting datasets
        // This ensures resolveUrl() uses direct blob URLs when datasets paths are resolved
        await initBlobDirectAccess()
        
        setAvailableDatasets(manifest.datasets)
        
        // Populate available shops from datasets
        const shopIds = manifest.datasets.map(dataset => dataset.id)
        console.log('📦 [App] Available shops:', shopIds)
        setAvailableShops(shopIds)
      } catch (error) {
        console.error('❌ [App] Failed to load datasets:', error)
      } finally {
        setIsLoadingDatasets(false)
        setDatasetsReady(true)
      }
    }
    
    loadDatasets()
  }, [setAvailableDatasets, setAvailableShops, setIsLoadingDatasets])

  // Load initial shop from URL parameter (only after datasets are loaded)
  // Note: GestureViewer will handle the actual loading, this just ensures datasets are ready
  useEffect(() => {
    if (initialShopId && availableDatasets.length > 0) {
      console.log(`🔗 [App] Shop ID detected in URL: ${initialShopId}`)
      console.log(`🔗 [App] Available datasets:`, availableDatasets.map(d => d.id))
      // Don't call loadDataset here - GestureViewer will handle it when it mounts
    }
  }, [initialShopId, availableDatasets])

  const [searchParams] = useSearchParams()
  const directMorphUrl = searchParams.get('morphUrl')

  return (
    <ErrorBoundary>
      <div className="app">
        {directMorphUrl ? (
          // Scanner-uploaded shop — open morph viewer directly via pre-built URL
          <iframe
            src={directMorphUrl}
            style={{ position: 'fixed', inset: 0, width: '100%', height: '100%', border: 'none' }}
            allow="fullscreen"
            title="Shop Viewer"
          />
        ) : effectiveShopId ? (
          // Shop ID is present — show viewer once datasets are loaded
          availableDatasets.length > 0 ? (
            <MorphViewerPage shopId={effectiveShopId} />
          ) : datasetsReady ? (
            // Datasets fetched but empty or shop not found — redirect to browse
            <RedirectToBrowse navigate={navigate} />
          ) : (
            // Still loading datasets — show loading state (don't redirect!)
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh', background: '#0f172a', color: '#94a3b8', fontSize: 16 }}>
              Loading shop data...
            </div>
          )
        ) : (
          // No shop ID in URL — redirect to browse
          <RedirectToBrowse navigate={navigate} />
        )}
      </div>
    </ErrorBoundary>
  )
}

/** Redirect to /browse when no shop is selected (replaces old ShopSelector fallback) */
function RedirectToBrowse({ navigate }: { navigate: ReturnType<typeof useNavigate> }) {
  useEffect(() => {
    navigate('/browse', { replace: true })
  }, [navigate])
  return null
}

export default App
